const MOBILE_BREAKPOINT = '(max-width: 639px)'
const DESKTOP_BREAKPOINT = '(min-width: 640px)'

export const useBannerGalleryVideoPlayback = (
  videoCardIndices: ComputedRef<number[]>
) => {
  const desktopPlayingCardIndex = ref<number | null>(null)
  const mobilePlaylistSlot = ref(0)
  const mobileVideoElements = ref<Record<number, HTMLVideoElement>>({})

  const mobileActiveCardIndex = computed(
    () => videoCardIndices.value[mobilePlaylistSlot.value] ?? null
  )

  const isMobile = () =>
    import.meta.client && window.matchMedia(MOBILE_BREAKPOINT).matches

  const isDesktop = () =>
    import.meta.client && window.matchMedia(DESKTOP_BREAKPOINT).matches

  const playlistKey = (indices: number[]) => indices.join(',')

  /** Ref callbacks must not trigger reactive updates that re-run render. */
  const registerMobileVideo = (
    cardIndex: number,
    el: Element | ComponentPublicInstance | null
  ) => {
    if (el instanceof HTMLVideoElement) {
      if (mobileVideoElements.value[cardIndex] === el) return
      mobileVideoElements.value[cardIndex] = el
      return
    }

    if (!el && mobileVideoElements.value[cardIndex]) {
      delete mobileVideoElements.value[cardIndex]
    }
  }

  const getDesktopVideo = (cardEl: HTMLElement) =>
    cardEl.querySelector<HTMLVideoElement>('.banner-gallery-video--desktop')

  const onDesktopCardEnter = (cardEl: HTMLElement, cardIndex: number) => {
    if (!isDesktop()) return

    const video = getDesktopVideo(cardEl)
    if (!video) return

    desktopPlayingCardIndex.value = cardIndex
    if (video.ended) {
      video.currentTime = 0
    }
    void video.play()
  }

  const onDesktopCardLeave = (cardEl: HTMLElement, cardIndex: number) => {
    if (!isDesktop()) return

    const video = getDesktopVideo(cardEl)
    if (!video) return

    if (desktopPlayingCardIndex.value === cardIndex) {
      desktopPlayingCardIndex.value = null
    }
    video.pause()
    video.currentTime = 0
  }

  const pauseAllMobileVideos = () => {
    Object.values(mobileVideoElements.value).forEach((video) => {
      video.pause()
      video.currentTime = 0
    })
  }

  const playMobileActiveVideo = async () => {
    if (!isMobile() || videoCardIndices.value.length === 0) return

    const activeIndex = mobileActiveCardIndex.value
    if (activeIndex === null) return

    Object.entries(mobileVideoElements.value).forEach(([idx, video]) => {
      if (Number(idx) !== activeIndex) {
        video.pause()
        video.currentTime = 0
      }
    })

    const video = mobileVideoElements.value[activeIndex]
    if (!video) return

    if (video.ended) {
      video.currentTime = 0
    }

    try {
      await video.play()
    } catch {
      // Autoplay policies — canplay handler may retry
    }
  }

  const scheduleMobilePlayback = () => {
    nextTick(() => void playMobileActiveVideo())
  }

  const startMobilePlaylist = () => {
    if (videoCardIndices.value.length === 0) return
    mobilePlaylistSlot.value = 0
    scheduleMobilePlayback()
  }

  const stopMobilePlaylist = () => {
    pauseAllMobileVideos()
  }

  const onMobileVideoEnded = (cardIndex: number) => {
    if (!isMobile()) return
    if (mobileActiveCardIndex.value !== cardIndex) return

    const total = videoCardIndices.value.length
    if (total === 0) return

    if (total === 1) {
      const video = mobileVideoElements.value[cardIndex]
      if (!video) return
      video.currentTime = 0
      void video.play()
      return
    }

    mobilePlaylistSlot.value = (mobilePlaylistSlot.value + 1) % total
    scheduleMobilePlayback()
  }

  const onMobileVideoCanPlay = (cardIndex: number) => {
    if (!isMobile()) return
    if (mobileActiveCardIndex.value !== cardIndex) return
    const video = mobileVideoElements.value[cardIndex]
    if (video?.paused) {
      void video.play()
    }
  }

  const bindMobileLayoutListener = () => {
    if (!import.meta.client) return

    const mediaQuery = window.matchMedia(MOBILE_BREAKPOINT)

    const onChange = (event: MediaQueryListEvent) => {
      if (event.matches) {
        startMobilePlaylist()
        return
      }
      desktopPlayingCardIndex.value = null
      stopMobilePlaylist()
    }

    mediaQuery.addEventListener('change', onChange)

    if (mediaQuery.matches) {
      startMobilePlaylist()
    }

    onUnmounted(() => {
      mediaQuery.removeEventListener('change', onChange)
      stopMobilePlaylist()
    })
  }

  let lastPlaylistKey = ''

  watch(
    () => playlistKey(videoCardIndices.value),
    (key) => {
      if (key === lastPlaylistKey) return
      lastPlaylistKey = key

      const indices = videoCardIndices.value
      if (indices.length === 0) {
        stopMobilePlaylist()
        return
      }
      if (mobilePlaylistSlot.value >= indices.length) {
        mobilePlaylistSlot.value = 0
      }
      if (isMobile()) {
        scheduleMobilePlayback()
      }
    },
    { immediate: true }
  )

  return {
    desktopPlayingCardIndex,
    mobileActiveCardIndex,
    registerMobileVideo,
    onDesktopCardEnter,
    onDesktopCardLeave,
    onMobileVideoEnded,
    onMobileVideoCanPlay,
    bindMobileLayoutListener,
  }
}
